import React, { useEffect, useRef, useState } from 'react';
import { Table, Container, Button, ActionIcon, Tooltip, Image, Anchor } from '@mantine/core';
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, useLocation } from 'react-router-dom';
import { Group, Divider, Modal, TextInput, Text } from '@mantine/core';
import { useParams } from 'react-router-dom';
import './NoResponsePage.css';
import logo from '../assets/logo.png';
import {
  IconArrowLeft,
  IconDownload,
  IconShare
} from '@tabler/icons-react';
import { FooterCentered } from '../components/Footer';
import { saveAs } from 'file-saver';
import FilePreviewModal from '../components/FilePreviewModal';
import { getFileName } from '../utils/fileStorage';

const CsvTablePage = () => {
  const location = useLocation();
  const csvContent = location.state?.csvContent || '';
  const initialFileStore = location.state?.fileStore || null;
  const navigate = useNavigate();
  const backButtonRef = useRef(null);
  const params = new URLSearchParams(location.search);
  const encryptedId = params.get('formId'); // may be encrypted token or raw id
  const [id, setId] = useState(''); // decrypted form id used for backend calls
  const [isEncrypting, setIsEncrypting] = useState(false);
  const { token: shareToken } = useParams();
  const isSharedView = Boolean(shareToken);
  const [isLoading, setIsLoading] = useState(false);

  // Decrypt encryptedId when present and not a shared token
  const fetchDecryptedId = async (encId) => {
    try {
      const token = getTokenFromCookie();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/decryptId`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ encryptedId: encId }),
      });

      if (!response.ok) throw new Error('Failed to decrypt ID');
      const data = await response.json();
      return data.decryptedId;
    } catch (err) {
      console.error('Error decrypting ID:', err);
      throw err;
    }
  };

  useEffect(() => {
    if (isSharedView) return;
    if (!encryptedId) return;

    setIsLoading(true);
    fetchDecryptedId(encryptedId)
      .then((decrypted) => setId(decrypted))
      .finally(() => setIsLoading(false));
  }, [encryptedId, isSharedView, navigate]);
  
const [shareResOpened, setShareResOpened] = useState(false);
const [shareError, setShareError] = useState('');
const [shareError2, setShareError2] = useState('');
const [formName, setFormName] = useState('');
const [accessType, setAccessType] = useState('PUBLIC');
const [allowedEmails, setAllowedEmails] = useState('');
const [shareUrl, setShareUrl] = useState('');
const [shareLoading, setShareLoading] = useState(false);
const [linkGenerated, setLinkGenerated] = useState(false);
const [publicShareOpened, setPublicShareOpened] = useState(false);
const [publicEmails, setPublicEmails] = useState('');
const [mailLoading, setMailLoading] = useState(false);
const [generatedAccessType, setGeneratedAccessType] = useState(null);
const [fileStore, setFileStore] = useState({});
const [previewOpen, setPreviewOpen] = useState(false);
const [previewFile, setPreviewFile] = useState(null);


useEffect(() => {
  if (!shareToken) return;
  setIsLoading(true);
  fetch(
    `${process.env.REACT_APP_API_BASE_URL}/shared-responses/${shareToken}`,
    {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${getTokenFromCookie() || ''}`,
      },
    }
  )
    .then(res => {
      if (!res.ok) throw new Error('Access denied');
      return res.json();
    })
    .then(data => {
      console.log('RAW shared response payload:', data);
      setFormName(data.formName);

        // convert backend response → CSV
        const normalizedResponses = normalizeFormData( data.responses.formData );
        console.log('Normalized responses:', normalizedResponses);
        const csv = generateAggregatedCSV(normalizedResponses);
        console.log('Generated CSV:', csv); 
      navigate(location.pathname + location.search, {
        replace: true,
        state: { csvContent: csv },
      });
    })
    .catch(() => {
      navigate('/TimeOut');
    })
    .finally(() => {
      setIsLoading(false); 
    });
}, [shareToken]);


const normalizeFormData = (formData = []) => {
  const map = {};

  formData.forEach(submission => {
    submission.answers.forEach(ans => {
      if (!map[ans.title]) {
        map[ans.title] = {
          title: ans.title,
          type: ans.type,
          answers: [],
          timestamps: []
        };
      }

      map[ans.title].answers.push(ans.answer);
      map[ans.title].timestamps.push(submission.timestamp);
    });
  });

  return Object.values(map);
};

const formatTimestamp = (ts) => {
  const date = new Date(ts || new Date());

  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const yearPart = date.toLocaleDateString('en-US', {
    year: 'numeric',
  });

  const timePart = date.toLocaleTimeString();

  return `${datePart} ${yearPart} [${timePart}]`;
};


const generateAggregatedCSV = (responses = []) => {
  if (!responses.length) return '';
  const localFileStore = {};

  const questionTitles = responses.map(r => r.title);
  const headers = ['Response Number', 'Timestamp', ...questionTitles];

  const responsesByTimestamp = {};

  responses.forEach((response, responseIndex) => {
    response.answers.forEach((answer, answerIndex) => {
      const rawTimestamp = response.timestamps?.[answerIndex];
      const timestamp = rawTimestamp?.includes('[')
          ? rawTimestamp
          : formatTimestamp(rawTimestamp);

      if (!responsesByTimestamp[timestamp]) {
        responsesByTimestamp[timestamp] = {};
      }

      let processedAnswer = '';

      // FILE UPLOAD
      if (response.type === 'file' && Array.isArray(answer)) {
      processedAnswer = answer
        .map((file, fileIndex) => {
          if (!file?.name) return '';

          const fileId = `${responseIndex}_${fileIndex}_${file.name}`;

          // 🔐 store full file object
          localFileStore[fileId] = file;

          if (file.type?.startsWith('image/')) return `[Image:${fileId}] ${file.name}`;
          if (file.type?.startsWith('audio/')) return `[Audio:${fileId}] ${file.name}`;
          if (file.type?.startsWith('video/')) return `[Video:${fileId}] ${file.name}`;
          return `[File:${fileId}] ${file.name}`;
        })
        .join('; ');
    }
    
      else if (
        response.type === 'signature' ||
        (typeof answer === 'string' && answer.startsWith('data:image'))
      ) {
        processedAnswer = 'Signed';
      }

      // CHECKBOX / MULTI
      else if (Array.isArray(answer)) {
        processedAnswer = answer.join('; ');
      }

      // FALLBACK
      else {
        processedAnswer = answer?.toString() || '';
      }

      if (responsesByTimestamp[timestamp][responseIndex]) {
        responsesByTimestamp[timestamp][responseIndex] += `; ${processedAnswer}`;
      } else {
        responsesByTimestamp[timestamp][responseIndex] = processedAnswer;
      }
    });
  });

  const csvRows = [
    headers.map(h => `"${h.replace(/"/g, '""')}"`).join(',')
  ];

  Object.entries(responsesByTimestamp).forEach(([timestamp, answers], idx) => {
    const row = [idx + 1, timestamp];

    questionTitles.forEach((_, qIndex) => {
      const val = answers[qIndex] || '';
      row.push(`"${val.replace(/"/g, '""')}"`);
    });

    csvRows.push(row.join(','));
  });

  setFileStore(localFileStore);
  return csvRows.join('\n');
};

useEffect(() => {
  if (initialFileStore && Object.keys(fileStore).length === 0) {
    setFileStore(initialFileStore);
  }
}, [initialFileStore, fileStore]);

useEffect(() => {
  // Only rebuild when we have an encrypted csv from navigation and the decrypted id is ready
  if (!csvContent || Object.keys(fileStore).length > 0 || !id) return;

  // This CSV came from navigation, not shared API
  // So we MUST rebuild fileStore from raw responses again

  const rebuildFromResponses = async () => {
    try {
      const token = getTokenFromCookie();
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/getResponse`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ formId: id })
        }
      );

      const data = await res.json();

      // normalize + regenerate CSV (this sets fileStore)
      const normalized = normalizeFormData(data.data.formData);
      generateAggregatedCSV(normalized);

    } catch (e) {
      console.error('Failed to rebuild file store', e);
    }
  };

  rebuildFromResponses();
}, [csvContent, id]);

  useEffect(() => {
    if (!csvContent && !isSharedView) {
      navigate(`/Responses?formId=${id}`);
    } else {
      backButtonRef.current?.focus();
    }
  }, [csvContent, navigate, id, isSharedView]);

  useEffect(() => {
      if (id) {
        fetchFormName(id);
      }
    }, [id]);

  if (isLoading){ 
    return  <p style={{color: 'gray', fontSize: '25px', textAlign: 'center', marginTop: '120px'}}>Loading...</p>;
  };
  if (!csvContent) {
  return (
    <div className="no-response-container">
      <div className="message-box">
        <h4 className="title">No CSV Content to display</h4>
      </div>
    </div>
  );
}

  function getTokenFromCookie() {
    const cookies = document.cookie.split(';');
    const tokenCookie = cookies.find((cookie) => cookie.trim().startsWith('token='));
    if (tokenCookie) {
      return tokenCookie.split('=')[1];
    }
    return null;
  }

  const parseCsvContent = (content) => {
    // Simple CSV parser that handles quoted fields
    const rows = [];
    let currentRow = [];
    let currentField = '';
    let insideQuotes = false;

    for (let i = 0; i < content.length; i++) {
      const char = content[i];

      if (char === '"') {
        insideQuotes = !insideQuotes;
      } else if (char === ',' && !insideQuotes) {
        currentRow.push(currentField);
        currentField = '';
      } else if (char === '\n' && !insideQuotes) {
        currentRow.push(currentField);
        rows.push(currentRow);
        currentRow = [];
        currentField = '';
      } else {
        currentField += char;
      }
    }

    // Push the last field and row
    if (currentField || currentRow.length > 0) {
      currentRow.push(currentField);
      rows.push(currentRow);
    }
    return rows;
  };

  const renderTableHeader = (headers) => (
  <tr>
    {headers.map((header, index) => (
      <th 
        key={index}
        style={{
          minWidth: '150px',          // Minimum header width
          maxWidth: '300px',         // Maximum header width
          padding: '5px 15px',      // More padding for headers
          fontSize: '14px',        // Slightly larger font
          fontWeight: 'bold',
        }}
      >
        {header}
      </th>
    ))}
  </tr>
);

   const renderTableBody = (rows) =>
  rows.slice(1).map((row, rowIndex) => (
    <tr key={rowIndex}>
      {row.map((cell, cellIndex) => (
        <td 
          key={cellIndex}
          style={{
            whiteSpace: 'normal',      // Allow normal text wrapping
            verticalAlign: 'top',      // Align content to top
            minWidth: '150px',         // Minimum column width
            maxWidth: '300px',         // Maximum column width to prevent too wide columns
            width: 'auto',             // Auto width based on content
            wordWrap: 'break-word',    // Break long words
            padding: '8px 12px',       // Add more padding for better spacing
            lineHeight: '1.4',         // Better line spacing for readability
          }}
        >
             {renderCell(cell)}
        </td>
      ))}
    </tr>
  ));
  const rows = parseCsvContent(csvContent);

  const handleEncryptAndNavigateBack = () => {
    try {
      setIsEncrypting(true);
      navigate(`/Responses?formId=${encodeURIComponent(encryptedId || id || '')}`);
    } catch (error) {
      console.error('Error navigating back:', error);
    } finally {
      setIsEncrypting(false);
    }
  };

const FILE_MARKER_REGEX = /^\[(Image|Audio|Video|File):(.+?)\]\s(.+)$/;
const renderCell = (cell) => {
  if (!cell) return null;

  // SIGNATURE
  if (typeof cell === 'string' && cell.startsWith('data:image')|| cell.includes('base64')) {
    return <div>Signed</div>;
  }

  return cell.split(';').map((raw, idx) => {
    const part = raw.trim();
    const match = part.match(FILE_MARKER_REGEX);

    if (match) {
      const [, kind, fileId, name] = match;
      const file = fileStore[fileId];

      if (!file) {
        return <div key={idx}>{name}</div>;
      }

      return (
        <div key={idx}>
          <FilePreviewLink file={file} kind={kind} />
        </div>
      );
    }

    return <div key={idx}>{part}</div>;
  });
};
const FilePreviewLink = ({ file, kind }) => {
  const openFile = () => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  return (
    <span
      onClick={openFile}
      style={{
        color: '#1b6c2aff',
        cursor: 'pointer',
        textDecoration: 'underline'
      }}
    >
      [{kind}] {getFileName(file)}
    </span>
  );
};
const closePreview = () => {
  setPreviewOpen(false);
  setPreviewFile(null);
};

// const renderCell = (cell) => {
//   if (!cell) return null;
//   // Signature placeholder
//   if (typeof cell === 'string' && cell.startsWith('data:image')) {
//     return <div>Signed</div>;
//   }
//   // Normal text split
//   if (/[;,]/.test(cell)) {
//     return cell.split(/[;,]/).map((item, i) => (
//       <div key={i}>{item.trim()}</div>
//     ));
//   }
//   return cell;
// };

const fetchFormName = async (formId) => {
  try {
    const token = getTokenFromCookie();

    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/ArrayForm?id=${formId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await res.json();
    setFormName(data?.formName || 'Untitled Form');
  } catch (err) {
    console.error('Error fetching form name:', err);
    setFormName('Untitled Form');
  }
};

const getSafeFileName = (name = 'untitled-form') =>
  name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-_]/g, '');

const handleDownloadCSV = () => {
  try {
    const safeName = getSafeFileName(formName);

    // REMOVE file IDs for export
    const cleanedCsv = csvContent.replace(
      /\[(Image|Audio|Video|File):.+?\]/g,
      '[$1]'
    );
    const blob = new Blob([cleanedCsv], {
      type: 'text/csv;charset=utf-8',
    });

    saveAs(blob, `${safeName}_responses.csv`);
  } catch (error) {
    console.error('CSV download failed:', error);
  }
};

const isAllowedEmail = (email) => {
  const lower = email.toLowerCase();
  return (
    lower.endsWith('@navriti.com') ||
    lower.endsWith('.navriti@gmail.com')
  );
};

const handleGenerateShareLink = async () => {
  try {
    setShareLoading(true);
    setShareError2('');

    const token = getTokenFromCookie();

    const emailsArray =
      accessType === 'RESTRICTED'
        ? allowedEmails
            .split(',')
            .map(e => e.trim())
            .filter(Boolean)
        : [];
    console.log("Restricted Emails",emailsArray);
    if (accessType === 'RESTRICTED' && emailsArray.length === 0) {
        setShareError2('Email is required');
        setShareLoading(false);
        return;
      }
      
    const invalidEmail = emailsArray.find(email => !isAllowedEmail(email));
      if (invalidEmail) {
        setShareError2(
          'Only *@navriti.com or *.navriti@gmail.com emails are allowed'
        );
        setShareLoading(false);
        return;
      }

    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/forms/${id}/share`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accessType,
          allowedEmails: emailsArray
        })
      }
    );

    if (!res.ok) {
      throw new Error('Failed to create share link');
    }

    const data = await res.json();
    setShareUrl(data.shareUrl);
    setLinkGenerated(true);
    setGeneratedAccessType(accessType);
    console.log("URL: ",data.shareUrl);
  } catch (err) {
    console.error(err);
    setShareError2('Failed to generate link');
  } finally {
    setShareLoading(false);
  }
};

const sendRestrictedEmails = async () => {
  try {
    setMailLoading(true);

    await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/forms/${id}/share/send-mail`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getTokenFromCookie()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'RESTRICTED',
          emails: allowedEmails.split(',').map(e => e.trim()),
          shareUrl,
          formName
        })
      }
    );

    toast.success('Emails sent successfully');
  } catch {
    toast.error('Failed to send emails');
  } finally {
    setMailLoading(false);
  }
};

const sendPublicEmails = async () => {
  const emails = publicEmails
    .split(',')
    .map(e => e.trim())
    .filter(Boolean);

  if (!emails.length) {
    setShareError('Please enter at least one email');
    return;
  }

  try {
    setMailLoading(true);

    await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/forms/${id}/share/send-mail`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getTokenFromCookie()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'PUBLIC',
          emails,
          shareUrl,
          formName
        })
      }
    );

    toast.success('Emails sent successfully');
    setPublicShareOpened(false);
    setPublicEmails('');
  } catch {
    toast.error('Failed to send emails');
  } finally {
    setMailLoading(false);
  }
};

    const openShareResponses = () => {
      setAllowedEmails('');
      setAccessType('PUBLIC');
      setShareResOpened(true);
    };


  return (
  <div style={{ 
    padding: '20px', 
    backgroundColor: '#fff',
    width: '100vw',           // Full viewport width
    minWidth: '1200px',       // Minimum width to prevent cramping
    overflowX: 'auto'         // Horizontal scroll if needed
  }}>
    <Container 
      fluid                   // Make container fluid to use full width
      style={{ maxWidth: 'none' }} // Remove max-width constraint
    >
            
      <Group justify="space-between" align="center" mb="md">
        {!isSharedView && (
        <Button
          ref={backButtonRef}
          onClick={handleEncryptAndNavigateBack}
          variant="light"
          leftSection={<IconArrowLeft size={16} />}
          radius="md"
          color="dark"
        >
          Back
        </Button>)}

        <Group gap="sm">
        {isSharedView && (
        <a href='/'><Image src={logo} height={50} width={50} /></a>)}
        {formName &&
        <h3 style={{textTransform: 'capitalize'}}>{formName}</h3>}</Group>

        <Group gap="sm">
          {!isSharedView && (<>
          <Button
            onClick={openShareResponses}
            variant="outline"
            leftSection={<IconShare size={16} />}
            radius="md"
            color="#008571ff"
          >
            Share Responses
          </Button> 
          <Button
            onClick={handleDownloadCSV}
            leftSection={<IconDownload size={16} />}
            radius="md"
            color="#008571ff"
          >
            Download CSV
          </Button></>
         )}
         {isSharedView && (
         <Tooltip label="Download CSV" withArrow>
            <ActionIcon
              onClick={handleDownloadCSV}
              radius="md"
              color="#008571ff"
              variant="filled"
              aria-label="Download CSV"
              size="lg"
            >
              <IconDownload size={21} />
            </ActionIcon>
          </Tooltip> )}
        </Group>
      </Group>

      <Divider mb="lg" />


      <div style={{ overflowX: 'auto' }}> {/* Add scroll wrapper */}
        <Table
          withBorder
          withColumnBorders
          highlightOnHover
          style={{
            border: '2px solid #ddd',
            borderCollapse: 'collapse',
            width: '100%',
            minWidth: '1000px',    // Minimum table width
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            backgroundColor: '#f5fffaff',
            tableLayout: 'auto',
            marginBottom: '40px',
          }}
        >
          <thead
            style={{
              backgroundColor: '#1ab394',
              textAlign: 'center',
              color: '#fff',
              fontWeight: 'bold',
              textTransform: 'capitalize'
            }}
          >
            {rows[0] && renderTableHeader(rows[0])}
          </thead>
          <tbody style={{ textAlign: 'center' }}>
            {rows.length > 1 && renderTableBody(rows)}
          </tbody>
        </Table>
      </div>
    </Container>
    

    <Modal
      opened={shareResOpened}
      onClose={() => setShareResOpened(false)}
      title="Share Response"
      centered
      overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }} >
      <Text size="sm" mb="xs">
        Choose who can access the responses
      </Text>

      <Group mt="sm">
        <Button
          variant={accessType === 'PUBLIC' ? 'filled' : 'outline'}
          onClick={() => setAccessType('PUBLIC')}
        >
          Public
        </Button>

        <Button
          variant={accessType === 'RESTRICTED' ? 'filled' : 'outline'}
          onClick={() => setAccessType('RESTRICTED')}
        >
          Restricted
        </Button>
      </Group>

      {accessType === 'RESTRICTED' && (
        <TextInput
          mt="md"
          label="Allowed emails (comma separated)"
          placeholder="user@navriti.com"
          value={allowedEmails}
          error={shareError2}
          onChange={(e) => setAllowedEmails(e.target.value)}
        />
      )}

      {shareUrl && generatedAccessType === accessType && (
          <Group mt="md" gap="xs" justify='center'>
            <Anchor
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              truncate
            >
              {shareUrl}
            </Anchor>

            {/* <Tooltip label="Copy link" withArrow>
              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={() => navigator.clipboard.writeText(shareUrl)}
                aria-label="Copy link" 
              >
                <IconCopy size={18} />
              </ActionIcon>
            </Tooltip>  */}
          </Group>
        )}

{linkGenerated &&
 generatedAccessType === 'PUBLIC' &&
 accessType === 'PUBLIC' && (
  <Group justify="space-between" mt="lg">
        <Button 
          variant='outline'
          onClick={() => {
            navigator.clipboard.writeText(shareUrl);
            toast.success('Link copied to clipboard');
          }}
          >
          Copy
        </Button>
        <Button
          color="green"
          onClick={() => setPublicShareOpened(true)}
        >
          Share via Email
        </Button>
  </Group>
)}
{linkGenerated &&
 generatedAccessType === 'RESTRICTED' &&
 accessType === 'RESTRICTED' && (
  <Group justify="space-between" mt="lg">
        <Button 
          variant='outline'
          onClick={() => {
            navigator.clipboard.writeText(shareUrl);
            toast.success('Link copied to clipboard');
          }}
          >
          Copy
        </Button>
        <Button
          color="green"
          loading={mailLoading}
          onClick={sendRestrictedEmails}
        >
          Send Email
        </Button>
  </Group>
)}

      <Divider mt="sm" />
      <Group justify="space-between" mt="lg">
        <Button variant="default" onClick={() => setShareResOpened(false)}>
          Close
        </Button>
        {/* {shareUrl &&
        <Button 
          variant='outline'
          onClick={() => {
            navigator.clipboard.writeText(shareUrl);
            toast.success('Link copied to clipboard');
          }}
          >
          Copy
        </Button>} */}

        <Button
          color="#008571ff"
          loading={shareLoading}
          onClick={handleGenerateShareLink}
        >
          Generate Link
        </Button>
      </Group>

    </Modal>
          <Modal
            opened={publicShareOpened}
            onClose={() => setPublicShareOpened(false)}
            title="Share Public Link"
            centered
          >
            <TextInput
              label="Email IDs (comma separated)"
              placeholder="abc@gmail.com"
              value={publicEmails}
              error={shareError}
              onChange={(e) => setPublicEmails(e.target.value)}
            />

            <Group justify="flex-end" mt="md">
              <Button
                loading={mailLoading}
                onClick={sendPublicEmails}
              >
                Send
              </Button>
            </Group>
          </Modal>

          <FilePreviewModal
            file={previewFile}
            opened={previewOpen}
            onClose={closePreview}
            shareToken={shareToken}
          />

          <FooterCentered />
          <ToastContainer />
  </div>
);
};

export default CsvTablePage;
